/**
 * Report-shaped derivations for the Pendo events (spec 094).
 *
 * These live OUTSIDE `./pendo.js` on purpose. `pendo.js` is imported by
 * `src/App.jsx`, which eagerly imports every page and is therefore the root
 * chunk; `pages/reports/registry` statically pulls in all 24 specialized report
 * components, and `reportGridUtils` is a 800-line grid utility. Importing
 * either from the analytics core would pull that graph into the root AND create
 * a circular-import hazard for any component that calls `trackTransform`.
 *
 * Everything here is a pure function so it can be pinned by tests directly.
 *
 * ⚠️ This module is NOT safe to import from a shared report component. Because
 * it imports the registry, and the registry statically imports all 24
 * specialized page components, doing so forms a cycle
 * (`registry → ARReport → SpecializedReportShell → here → registry`) that
 * leaves `REPORT_RENDERERS` half-built. `errorClass` used to live here and was
 * moved to `./errorClass.js` — a leaf — for exactly that reason. Anything a
 * shared component needs belongs there, not here.
 */

import { resolveFilterLabel } from '@components/reports/reportGridUtils'
import { getRendererForActionKey } from '@pages/reports/registry'

/** Property-length ceiling for the joined filter-name list. */
const FILTERS_USED_MAX = 480

/**
 * Which renderer actually drew this report.
 *
 * "Specialized" is REGISTRY MEMBERSHIP, not `actionKey` truthiness. Generic
 * reports carry `actionKey: ""`, and an unregistered NON-EMPTY actionKey still
 * renders generic. This calls the very predicate `ReportView` uses to pick the
 * component (`registry.getRendererForActionKey`), so the reported surface can
 * never disagree with what was on screen — including any quirk of that lookup.
 *
 * @returns {'specialized'|'generic'}
 */
export const reportSurface = (actionKey) =>
  getRendererForActionKey(actionKey) ? 'specialized' : 'generic'

/**
 * Shape of a RESULT, not of a report.
 *
 * `isFlatReport` / `isGroupedReport` in ReportView both test `report.reportData`
 * for `isGroup` rows and are NOT complementary — an empty `reportData` is
 * neither and falls through to the empty-result renderer. The same report can
 * change bucket between runs, and client-side Group By can turn a flat result
 * grouped. So this is knowable only on completion, never from the definition,
 * and never from `definition.reportGroupData`.
 *
 * `unknown` is kept distinct from `empty`: a result we could not read is not a
 * report with no rows. Conflating them is the `|| []` family of bug that has
 * cost this repo six separate fixes.
 *
 * @returns {'flat'|'grouped'|'empty'|'unknown'}
 */
export function resultShape(report) {
  const rows = report?.reportData
  if (!Array.isArray(rows)) return 'unknown'
  if (rows.length === 0) return 'empty'
  return rows.some((r) => r?.isGroup) ? 'grouped' : 'flat'
}

/**
 * Sorted, comma-joined list of filter NAMES. Never values.
 *
 * 🔴 Generic filter values are client names, matter names, timekeeper names and
 * firm date ranges. Only the names of the filters the user touched leave the
 * browser. Sorting makes filter COMBINATIONS groupable in Data Explorer.
 *
 * Labels resolve through `resolveFilterLabel`, NOT `col.label`. CB definitions
 * carry two columns for one concept sharing a `field` — an abbreviated
 * filterable one ('RespAtty') and a display-only twin ('Responsible
 * Attorney') — and `resolveFilterLabel` prefers the readable twin, which is
 * what the filter panel, the applied-criteria pill and the export header all
 * show (CC-50009). Using `col.label` here would silently split one filter into
 * two different names in every Group By, and disagree with the screen.
 *
 * @param {Array<{columnId: number|string, value: string}>} wireFilters as sent to CB
 * @param {Array<object>} columns definition.columns
 * @returns {string}
 */
export function filterNames(wireFilters, columns) {
  if (!Array.isArray(wireFilters)) return ''
  const cols = Array.isArray(columns) ? columns : []
  const byId = new Map(cols.map((c) => [String(c?.columnId), c]))

  const names = []
  for (const f of wireFilters) {
    if (!f || f.value === '' || f.value == null) continue
    const col = byId.get(String(f.columnId))
    // No matching column: report the id, never the value. `col:<id>` is an
    // internal identifier with no firm data in it, and seeing it in Pendo is
    // the signal that a definition and a filter set have drifted apart.
    names.push(col ? resolveFilterLabel(col, cols) : `col:${f.columnId}`)
  }

  const joined = Array.from(new Set(names)).sort().join(',')
  return joined.length > FILTERS_USED_MAX ? `${joined.slice(0, FILTERS_USED_MAX - 3)}...` : joined
}
